import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import { FaUser, FaEnvelope, FaComment, FaLightbulb, FaDownload } from "react-icons/fa";
import Logo from "../components/Logo";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import LoadingIndicator from "../components/LoadingIndicator";
import "../styles/AddSuggestions.css";

function AddSuggestions() {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        message: ""
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };



    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");

        // Validate form
        if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
            setError("Please fill in all required fields");
            setLoading(false);
            return;
        }

        // Execute reCAPTCHA v3
        if (!executeRecaptcha) {
            setError("reCAPTCHA not ready. Please try again.");
            setLoading(false);
            return;
        }

        const recaptchaToken = await executeRecaptcha('submit_suggestion');
        if (!recaptchaToken) {
            setError("reCAPTCHA verification failed. Please try again.");
            setLoading(false);
            return;
        }

        try {
            const response = await api.post("/api/suggestion/", {
                ...formData,
                recaptcha: recaptchaToken
            });

            if (response.data.success) {
                setSuccess(true);
                setFormData({
                    name: "",
                    email: "",
                    message: ""
                });

            }
        } catch (error) {
            if (error.response?.data) {
                const errorData = error.response.data;
                if (typeof errorData === 'object') {
                    const errorMessages = [];
                    Object.keys(errorData).forEach(field => {
                        if (Array.isArray(errorData[field])) {
                            errorMessages.push(...errorData[field]);
                        } else {
                            errorMessages.push(errorData[field]);
                        }
                    });
                    setError(errorMessages.join(' '));
                } else {
                    setError(errorData);
                }
            } else {
                setError("An error occurred. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="add-suggestions-container">
                <header className="add-suggestions-header">
                    <Logo size="small" showText={false} linkTo="/" />
                    <nav className="nav-links">
                        <Link to="/downloads" className="download-nav-link"><FaDownload /> Download</Link>
                        <Link to="/login" className="login-link">Login</Link>
                        <Link to="/register" className="register-link">Register</Link>
                    </nav>
                </header>

                <div className="add-suggestions-content">
                    <div className="success-card">
                        <div className="success-icon">
                            <FaLightbulb />
                        </div>
                        <h2>Suggestion Submitted Successfully!</h2>
                        <p>Thank you for your suggestion. We appreciate your feedback and will consider it for future improvements.</p>
                        <div className="success-actions">
                            <Link to="/" className="btn btn-primary">Go Home</Link>
                            <button 
                                onClick={() => setSuccess(false)} 
                                className="btn btn-outline"
                            >
                                Add Another Suggestion
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="add-suggestions-container">
            <header className="add-suggestions-header">
                <Logo size="small" showText={false} linkTo="/" />
                <nav className="nav-links">
                    <Link to="/downloads" className="download-nav-link"><FaDownload /> Download</Link>
                    <Link to="/login" className="login-link">Login</Link>
                    <Link to="/register" className="register-link">Register</Link>
                </nav>
            </header>

            <div className="add-suggestions-content">
                <div className="add-suggestions-card">
                    <div className="add-suggestions-header-content">
                        <FaLightbulb className="suggestion-icon" />
                        <h2>Add Suggestions</h2>
                        <p>Share your ideas to help us make triagz better</p>
                    </div>

                    <form onSubmit={handleSubmit} className="add-suggestions-form">
                        {error && <div className="error-message">{error}</div>}

                        <div className="form-group">
                            <label htmlFor="name">Name <span className="required">*</span></label>
                            <div className="input-group">
                                <FaUser className="input-icon" />
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter your name"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email Address <span className="required">*</span></label>
                            <div className="input-group">
                                <FaEnvelope className="input-icon" />
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter your email address"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="message">Your Suggestion <span className="required">*</span></label>
                            <div className="input-group">
                                <FaComment className="input-icon" />
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    placeholder="Please share your suggestion or feature request. Be as detailed as possible to help us understand your idea."
                                    rows={5}
                                    required
                                />
                            </div>
                        </div>

                        <button 
                            className="submit-button" 
                            type="submit" 
                            disabled={loading}
                        >
                            {loading ? <LoadingIndicator /> : "Submit Suggestion"}
                        </button>
                    </form>

                    <div className="form-footer">
                        <Link to="/">← Back to Home</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddSuggestions; 